/**
 * Component that renders table for the user search
 */
import { Delete } from "@mui/icons-material";
import {
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Grid,
	Paper,
	styled,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
} from "@mui/material";
import { types } from "api";
import { CONFIRM_DELETE } from "messages";
import React, { useState } from "react";

const PREFIX = "UserTable";

const classes = {
	paper: `${PREFIX}-paper`,
	errButton: `${PREFIX}-errButton`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
	[`&.${classes.paper}`]: {
		padding: theme.spacing(2),
		marginTop: theme.spacing(4),
	},

	[`& .${classes.errButton}`]: {
		backgroundColor: theme.palette.error.main,
		borderColor: theme.palette.error.main,
		color: "white",
		"&:hover": {
			borderColor: theme.palette.error.main,
			boxShadow: "none",
			color: theme.palette.error.main,
		},
	},
}));

export interface UserTableProps {
	userList: types.User[];
	userListHandler: (users: types.User[]) => void;
}
export const UserTable = (props: UserTableProps): JSX.Element => {
	const { userList, userListHandler } = props;
	const [usersSelected, setUsersSelected] = useState<types.User[]>([]);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(5);
	const [showDialog, setShowDialog] = useState<boolean>(false);

	const onSelectAllClick = (
		event: React.ChangeEvent<HTMLInputElement>,
	): void => {
		// selects all users when select all clicked
		event.target.checked && usersSelected.length === 0
			? setUsersSelected(userList)
			: setUsersSelected([]);
	};
	const handleCheckboxClick = (
		event: React.ChangeEvent<HTMLInputElement>,
		user: types.User,
	): void => {
		if (event.target.checked) {
			if (!usersSelected.includes(user)) {
				setUsersSelected([...usersSelected, user]);
			}
		} else {
			setUsersSelected(
				usersSelected.filter((item) => item.email !== user.email),
			);
		}
	};
	const handleChangePage = (event: unknown, newPage: number): void => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>,
	): void => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const deleteUser = (user: types.User): void => {
		userListHandler(userList.filter((item) => item.email !== user.email));
	};

	const deleteMultiple = (): void => {
		setShowDialog(false);
		userListHandler(userList.filter((item) => !usersSelected.includes(item)));
	};

	return (
		<StyledPaper className={classes.paper}>
			<TableContainer>
				<Table aria-label="simple table">
					<TableHead>
						<TableRow>
							<TableCell padding="checkbox">
								<Checkbox
									indeterminate={
										usersSelected.length > 0 &&
										usersSelected.length !== userList.length
									}
									checked={usersSelected.length === userList.length}
									onChange={onSelectAllClick}
									inputProps={{ "aria-label": "select all users" }}
								/>
							</TableCell>
							<TableCell align="right">First Name</TableCell>
							<TableCell align="right">Last Name</TableCell>
							<TableCell align="right">Email</TableCell>
							<TableCell align="right">Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{userList.length > 0 &&
							userList
								.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
								.map((user, index) => {
									const labelId = `enhanced-table-checkbox-${index}`;
									return (
										<TableRow
											key={user.email}
											hover
											role="checkbox"
											aria-checked={usersSelected.includes(user)}
											tabIndex={-1}
											selected={usersSelected.includes(user)}
										>
											<TableCell padding="checkbox">
												<Checkbox
													checked={usersSelected.includes(user)}
													inputProps={{ "aria-labelledby": labelId }}
													onChange={(event) => handleCheckboxClick(event, user)}
												/>
											</TableCell>
											<TableCell align="right">{user.firstName}</TableCell>
											<TableCell align="right">{user.lastName}</TableCell>
											<TableCell align="right">{user.email}</TableCell>
											<TableCell align="right">
												<Button
													variant="outlined"
													className={classes.errButton}
													color="error"
													startIcon={<Delete />}
													onClick={() => deleteUser(user)}
												>
													Delete
												</Button>
											</TableCell>
										</TableRow>
									);
								})}
					</TableBody>
				</Table>
			</TableContainer>
			<Dialog
				open={showDialog}
				onClose={() => setShowDialog(false)}
				aria-labelledby="alert-dialog-confirm"
				aria-describedby="alert-dialog-confirm-delete"
			>
				<DialogTitle id="alert-dialog-title">Are you sure?</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">
						{CONFIRM_DELETE}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setShowDialog(false)} color="primary">
						Cancel
					</Button>
					<Button onClick={deleteMultiple} color="primary" autoFocus>
						Delete
					</Button>
				</DialogActions>
			</Dialog>
			<TablePagination
				rowsPerPageOptions={[5, 10, 25]}
				rowsPerPage={rowsPerPage}
				page={page}
				component="div"
				count={userList.length}
				onPageChange={handleChangePage}
				onRowsPerPageChange={handleChangeRowsPerPage}
			/>
			<Grid container direction="row" justifyContent="flex-end">
				<Button
					className={classes.errButton}
					variant="contained"
					disabled={usersSelected.length === 0}
					onClick={() => setShowDialog(true)}
				>
					Delete Selected
				</Button>
			</Grid>
		</StyledPaper>
	);
};
